import React, { useState, useCallback, useEffect } from "react";
import Chessboard from "../components/Chessboard";
import { Chess } from "chess.js";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import { useAuth } from "../contexts/AuthContext";
import { updateUserStats, addRecentGame } from "../components/Chessboard";
import {
  undoMove,
  makeUserMove,
  makeBotMove,
  getResult
} from "../utils/chessGameUtils";
import PromotionModal from "../components/PromotionModal";

export default function PlayBot() {
  const { currentUser } = useAuth();
  const [game, setGame] = useState(new Chess());
  const [userTurn, setUserTurn] = useState(true);
  const [result, setResult] = useState(null);
  const [gameStarted, setGameStarted] = useState(false);
  const [selectedPiece, setSelectedPiece] = useState(null);
  const [availableMoves, setAvailableMoves] = useState([]);
  const [difficulty, setDifficulty] = useState("easy");
  const [fenHistory, setFenHistory] = useState([new Chess().fen()]);
  const [promotionInfo, setPromotionInfo] = useState(null);
  const [promotionOpen, setPromotionOpen] = useState(false);

  // Bot move logic
  function handleBotMove(currentGame) {
    const { move, newGame } = makeBotMove(currentGame, difficulty);
    if (move) {
      setFenHistory(prev => [...prev, currentGame.fen()]);
      setGame(newGame);
      setUserTurn(true);
      setSelectedPiece(null);
      setAvailableMoves([]);
      if (newGame.isGameOver()) {
        const resultStr = getResult(newGame);
        setResult(resultStr);
        if (currentUser) {
          let outcome;
          if (resultStr === "You win!") outcome = "win";
          else if (resultStr === "Bot wins!") outcome = "loss";
          else if (resultStr === "Draw!") outcome = "draw";
          if (outcome) {
            (async () => {
              try {
                await updateUserStats(currentUser.uid, outcome);
                const today = new Date();
                const dateStr = today.toISOString().slice(0, 10);
                let displayResult = outcome.charAt(0).toUpperCase() + outcome.slice(1);
                await addRecentGame(currentUser.uid, "Bot", displayResult, dateStr);
              } catch (err) {
                console.error("Failed to update stats or recent games:", err);
              }
            })();
          }
        }
      }
    }
  }

  // User move handler
  const handleUserMove = (from, to) => {
    if (!userTurn || game.isGameOver() || !gameStarted) return false;
    const moves = game.moves({ square: from, verbose: true });
    const isPromotion = moves.some(move => move.to === to && move.promotion);
    if (isPromotion) {
      setPromotionInfo({ from, to });
      setPromotionOpen(true);
      return false;
    }
    const { move, newGame } = makeUserMove(game, from, to);
    if (!move) return false;
    setFenHistory(prev => [...prev, game.fen()]);
    setGame(newGame);
    setUserTurn(false);
    setSelectedPiece(null);
    setAvailableMoves([]);
    if (newGame.isGameOver()) {
      const resultStr = getResult(newGame);
      setResult(resultStr);
      if (currentUser) {
        let outcome;
        if (resultStr === "You win!") outcome = "win";
        else if (resultStr === "Bot wins!") outcome = "loss";
        else if (resultStr === "Draw!") outcome = "draw";
        if (outcome) updateUserStats(currentUser.uid, outcome).then(() => {
          const today = new Date();
          const dateStr = today.toISOString().slice(0, 10);
          let displayResult = outcome.charAt(0).toUpperCase() + outcome.slice(1);
          addRecentGame(currentUser.uid, "Bot", displayResult, dateStr);
        });
      }
    } else {
      setTimeout(() => handleBotMove(new Chess(newGame.fen())), 800);
    }
    return true;
  };

  // Undo handler
  function handleUndo() {
    const { chess, newHistory } = undoMove(fenHistory);
    if (!chess) return;
    setGame(chess);
    setFenHistory(newHistory);
    setUserTurn(true);
    setSelectedPiece(null);
    setAvailableMoves([]);
    setResult(null);
  }

  // Piece selection
  const handlePieceSelect = useCallback((from) => {
    if (!userTurn || game.isGameOver() || !gameStarted) return;
    if (selectedPiece === from) {
      setSelectedPiece(null);
      setAvailableMoves([]);
      return;
    }
    const piece = game.get(from);
    if (piece && piece.color === 'w') {
      setSelectedPiece(from);
      const moves = game.moves({ square: from, verbose: true });
      setAvailableMoves(moves.map(move => move.to));
    }
  }, [game, userTurn, gameStarted, selectedPiece]);

  useEffect(() => {
    setSelectedPiece(null);
    setAvailableMoves([]);
  }, [game]);

  function handleStartGame() {
    setGame(new Chess());
    setUserTurn(true);
    setResult(null);
    setGameStarted(true);
    setSelectedPiece(null);
    setAvailableMoves([]);
    setFenHistory([new Chess().fen()]);
  }

  function handleReset() {
    setGame(new Chess());
    setUserTurn(true);
    setResult(null);
    setGameStarted(false);
    setSelectedPiece(null);
    setAvailableMoves([]);
    setFenHistory([new Chess().fen()]);
    const handlePromotionSelect = (promotionType) => {
  const { from, to } = promotionInfo;
  const { move, newGame } = makeUserMove(game, from, to, promotionType);
  if (!move) return;
  setFenHistory(prev => [...prev, game.fen()]);
  setGame(newGame);
  setUserTurn(false);
  setSelectedPiece(null);
  setAvailableMoves([]);
  setPromotionOpen(false);
  setPromotionInfo(null);
  if (newGame.isGameOver()) {
    const resultStr = getResult(newGame);
    setResult(resultStr);
    if (currentUser) {
      let outcome;
      if (resultStr === "You win!") outcome = "win";
      else if (resultStr === "Bot wins!") outcome = "loss";
      else if (resultStr === "Draw!") outcome = "draw";
      if (outcome) {
        (async () => {
          try {
            await updateUserStats(currentUser.uid, outcome);
            const today = new Date();
            const dateStr = today.toISOString().slice(0, 10);
            let displayResult = outcome.charAt(0).toUpperCase() + outcome.slice(1);
            await addRecentGame(currentUser.uid, "Bot", displayResult, dateStr);
          } catch (err) {
            console.error("Failed to update stats or recent games:", err);
          }
        })();
      }
    }
  } else {
    setTimeout(() => handleBotMove(new Chess(newGame.fen())), 800);
  }
};

  }
  
const handlePromotionSelect = (promotionType) => {
  const { from, to } = promotionInfo;
  const { move, newGame } = makeUserMove(game, from, to, promotionType);
  if (!move) return;
  setFenHistory(prev => [...prev, game.fen()]);
  setGame(newGame);
  setUserTurn(false);
  setSelectedPiece(null);
  setAvailableMoves([]);
  setPromotionOpen(false);
  setPromotionInfo(null);
  if (newGame.isGameOver()) {
    const resultStr = getResult(newGame);
    setResult(resultStr);
    if (currentUser) {
      let outcome;
      if (resultStr === "You win!") outcome = "win";
      else if (resultStr === "Bot wins!") outcome = "loss";
      else if (resultStr === "Draw!") outcome = "draw";
      if (outcome) {
        (async () => {
          try {
            await updateUserStats(currentUser.uid, outcome);
            const today = new Date();
            const dateStr = today.toISOString().slice(0, 10);
            let displayResult = outcome.charAt(0).toUpperCase() + outcome.slice(1);
            await addRecentGame(currentUser.uid, "Bot", displayResult, dateStr);
          } catch (err) {
            console.error("Failed to update stats or recent games:", err);
          }
        })();
      }
    }
  } else {
    setTimeout(() => handleBotMove(new Chess(newGame.fen())), 800);
  }
};
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 4 }}>
      <Card sx={{ p: 2, mb: 2, maxWidth: 700 }}>
        <CardContent>
          <Typography variant="h5" gutterBottom>Play Against Bot</Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            You play as White. The bot will make a move after each of your moves.
          </Typography>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="body2" sx={{ mr: 1, display: 'inline' }}>Difficulty:</Typography>
              <Select
                value={difficulty}
                onChange={e => setDifficulty(e.target.value)}
                size="small"
                sx={{ minWidth: 100 }}
              >
                <MenuItem value="easy">Easy</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="hard">Hard</MenuItem>
              </Select>
            </Box>
          </Box>
          {!gameStarted && (
            <Button 
              variant="contained" 
              color="primary" 
              fullWidth 
              onClick={handleStartGame}
              sx={{ mb: 2 }}
            >
              Start Game
            </Button>
          )}
          {gameStarted && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" color={userTurn ? 'success.main' : 'text.secondary'}>
                {userTurn ? "Your turn (White)" : "Bot's turn (Black)"}
              </Typography>
              {result && (
                <Typography variant="h6" color="primary" sx={{ mt: 1 }}>
                  {result}
                </Typography>
              )}
            </Box>
          )}
          {selectedPiece && availableMoves.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" gutterBottom>
                Available moves for {selectedPiece}:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {availableMoves.map((move, index) => (
                  <Chip 
                    key={index} 
                    label={move} 
                    size="small" 
                    color="primary" 
                    variant="outlined"
                  />
                ))}
              </Box>
            </Box>
          )}
          <Box sx={{ my: 2, display: 'flex', justifyContent: 'center' }}>
            <Chessboard
              customGame={game}
              onUserMove={handleUserMove}
              onPieceSelect={handlePieceSelect}
              selectedPiece={selectedPiece}
              availableMoves={availableMoves}
              disableMultiplayer
              hideChat
              hideMoveHistory
            />
          </Box>
          <Box sx={{ my: 2, display: 'flex', justifyContent: 'center', gap: 2 }}>
            <Button variant="outlined" color="secondary" onClick={handleReset}>
              Reset Board
            </Button>
            <Button variant="outlined" color="primary" onClick={handleUndo} disabled={fenHistory.length < 2 || !gameStarted}>
              Undo
            </Button>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            {gameStarted && (
              <Button 
                variant="outlined" 
                color="primary" 
                onClick={handleStartGame}
                fullWidth
              >
                New Game
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>
      <PromotionModal
        open={promotionOpen}
        color="w"
        onSelect={handlePromotionSelect}
      />
    </Box>
  );
} 